/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import MDButton from 'components/MDButton';
import MDDatePicker from 'components/MDDatePicker';
import MDTimePicker from 'components/MDTimePicker';
import { api } from '../../../api';
import {
  DROPDOWN_SX,
  TEAL,
  TIME_PICKER_STEP_MINUTES,
  formatDisplayDate,
  formatTimeFor24Hour,
  getCurrentUserId,
} from '../constants';

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 2;
const GUEST_MOBILE_CHECK_DEBOUNCE_MS = 500;
const GUEST_MOBILE_MIN_LENGTH = 6;
const GUEST_COUNTRY_CODE = '91';

function patientLabel(option) {
  const name = [option.first_name, option.middle_name, option.last_name].filter(Boolean).join(' ');
  const code = option.external_code || option.patientCouple?.external_code || option.spouseCouple?.external_code;
  const parts = [name || option.full_name || `Patient #${option.id}`];
  if (option.mobile_number) parts.push(option.mobile_number);
  if (code) parts.push(code);
  return `${parts[0]}${parts.length > 1 ? ` (${parts.slice(1).join(' · ')})` : ''}`;
}

export default function CreateAppointmentDialog({ open, onClose, onCreated }) {
  const [mode, setMode] = useState('existing'); // 'existing' | 'guest'
  const [patient, setPatient] = useState(null);
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientInput, setPatientInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestMobile, setGuestMobile] = useState('');
  const [guestMatches, setGuestMatches] = useState(null); // null = not checked, [] = clear, [...] = existing patient(s) found
  const [checkingGuestMobile, setCheckingGuestMobile] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.contacts
      .getDoctorsList()
      .then(resp => setDoctors(resp?.data || []))
      .catch(() => setDoctors([]));
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'existing' || patientInput.length < MIN_SEARCH_LENGTH) {
      setPatientOptions([]);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api.appointment
        .searchPatientForAppointment(patientInput)
        .then(resp => setPatientOptions(resp?.data || []))
        .catch(() => setPatientOptions([]))
        .finally(() => setSearching(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, mode, patientInput]);

  // Live courtesy check — a mobile that already belongs to a registered
  // patient can never be saved as a guest, so this surfaces the match(es)
  // before the receptionist even tries to submit. The authoritative gate is
  // still server-side (createAppointment's own 409 check).
  useEffect(() => {
    if (!open || mode !== 'guest' || guestMobile.length < GUEST_MOBILE_MIN_LENGTH) {
      setGuestMatches(null);
      return undefined;
    }
    setCheckingGuestMobile(true);
    const timer = setTimeout(() => {
      api.appointment
        .checkGuestMobile(guestMobile)
        .then(resp => setGuestMatches(resp?.data?.patients || []))
        .catch(() => setGuestMatches(null))
        .finally(() => setCheckingGuestMobile(false));
    }, GUEST_MOBILE_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, mode, guestMobile]);

  const resetForm = () => {
    setMode('existing');
    setPatient(null);
    setPatientOptions([]);
    setPatientInput('');
    setGuestName('');
    setGuestMobile('');
    setGuestMatches(null);
    setDoctorId('');
    setAppointmentDate(null);
    setAppointmentTime('');
    setReasonForVisit('');
    setNotes('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleModeChange = (_, value) => {
    if (!value) return;
    setMode(value);
    setGuestMatches(null);
  };

  // A matched existing patient is picked instead of the guest — swap back
  // into "existing patient" mode with that contact pre-selected.
  const selectExistingPatient = match => {
    setPatientOptions([match]);
    setPatient(match);
    setPatientInput(patientLabel(match));
    setGuestName('');
    setGuestMobile('');
    setGuestMatches(null);
    setMode('existing');
  };

  const handleSubmit = async () => {
    if (mode === 'existing') {
      if (!patient || !doctorId || !appointmentDate) {
        Swal.fire({ icon: 'warning', title: 'Missing details', text: 'Patient, doctor and date are required.' });
        return;
      }
    } else {
      if (!guestName.trim() || !guestMobile.trim() || !doctorId || !appointmentDate) {
        Swal.fire({ icon: 'warning', title: 'Missing details', text: 'Guest name, mobile, doctor and date are required.' });
        return;
      }
      if (guestMatches && guestMatches.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'This mobile number is already registered',
          text: 'Pick the matching patient below instead of booking as a guest.',
        });
        return;
      }
    }

    const confirmHtml =
      mode === 'existing'
        ? `<b>${patientLabel(patient)}</b><br/>with doctor on <b>${formatDisplayDate(appointmentDate)}</b>${appointmentTime ? ` at <b>${appointmentTime}</b>` : ''}`
        : `<b>${guestName}</b> (Guest, ${guestMobile})<br/>with doctor on <b>${formatDisplayDate(appointmentDate)}</b>${appointmentTime ? ` at <b>${appointmentTime}</b>` : ''}`;

    const confirmResult = await Swal.fire({
      title: 'Schedule this appointment?',
      html: confirmHtml,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Schedule',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;

    setSubmitting(true);
    try {
      const resp = await api.appointment.createAppointment({
        ...(mode === 'existing'
          ? { irm_contacts_id: patient.id }
          : { guest_name: guestName.trim(), guest_mobile: guestMobile.trim(), guest_country_code: GUEST_COUNTRY_CODE }),
        irm_users_id: doctorId,
        appointment_date: appointmentDate,
        appointment_time: formatTimeFor24Hour(appointmentTime),
        reason_for_visit: reasonForVisit || undefined,
        notes: notes || undefined,
        created_by: getCurrentUserId(),
      });
      Swal.fire({ icon: 'success', title: 'Appointment scheduled', timer: 1800, showConfirmButton: false });
      handleClose();
      onCreated?.(resp?.data);
    } catch (error) {
      // ApiClient already shows a generic error toast. If the mobile turned
      // out to be a registered patient after all (race with the live check
      // above), surface the picker instead of leaving the receptionist stuck.
      const existingPatients = error?.response?.data?.existing_patients;
      if (existingPatients?.length) {
        setGuestMatches(existingPatients);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Appointment</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            size="small"
            color="primary"
            fullWidth
          >
            <ToggleButton value="existing">Existing Patient</ToggleButton>
            <ToggleButton value="guest">Guest (Not Yet Registered)</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'existing' ? (
            <Autocomplete
              options={patientOptions}
              value={patient}
              loading={searching}
              // The server already filters by name/mobile/patient-code/couple-code —
              // MUI's default client-side re-filter only matches against the visible
              // label (name + mobile), so a code/couple-code match would otherwise
              // get silently hidden as "No options". Disable it.
              filterOptions={options => options}
              getOptionLabel={option => (option ? patientLabel(option) : '')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setPatient(value)}
              onInputChange={(_, value) => setPatientInput(value)}
              renderInput={params => <TextField {...params} label="Search patient (name / mobile / patient code / couple code)" size="small" />}
            />
          ) : (
            <>
              <TextField
                label="Guest Name"
                size="small"
                fullWidth
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
              />
              <TextField
                label="Mobile Number"
                size="small"
                fullWidth
                value={guestMobile}
                onChange={e => setGuestMobile(e.target.value.replace(/\D/g, ''))}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#888' }}>+{GUEST_COUNTRY_CODE}</Typography> }}
                helperText={checkingGuestMobile ? 'Checking if this number is already registered…' : ' '}
              />
              {guestMatches && guestMatches.length > 0 && (
                <Alert severity="warning">
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
                    This mobile number already belongs to a registered patient — a guest appointment cannot be saved with it. Pick the patient below instead:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {guestMatches.map(match => (
                      <Button
                        key={match.id}
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => selectExistingPatient(match)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {patientLabel(match)}
                      </Button>
                    ))}
                  </Box>
                </Alert>
              )}
            </>
          )}

          <FormControl fullWidth size="small">
            <InputLabel id="appt-doctor-label">Doctor</InputLabel>
            <Select
              labelId="appt-doctor-label"
              label="Doctor"
              value={doctorId}
              onChange={e => setDoctorId(e.target.value)}
              sx={DROPDOWN_SX}
            >
              {doctors.map(d => (
                <MenuItem key={d.user_id} value={d.user_id}>
                  {d.name} {d.occupation ? `(${d.occupation})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <MDDatePicker label="Appointment Date" value={appointmentDate} onChange={setAppointmentDate} />
            <MDTimePicker
              label="Time (optional)"
              value={appointmentTime}
              onChange={val => setAppointmentTime(val || '')}
              minutesStep={TIME_PICKER_STEP_MINUTES}
              textFieldProps={{ fullWidth: true }}
            />
          </Box>

          <TextField
            label="Reason for visit (optional)"
            size="small"
            fullWidth
            value={reasonForVisit}
            onChange={e => setReasonForVisit(e.target.value)}
          />
          <TextField
            label="Notes (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleClose} color="secondary">Cancel</MDButton>
        <MDButton
          onClick={handleSubmit}
          disabled={submitting || (mode === 'guest' && guestMatches && guestMatches.length > 0)}
          variant="gradient"
          color="logo"
        >
          {submitting ? 'Scheduling…' : 'Schedule'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
